import type {
  LoadingPolicyCatalog,
  LoadingPolicyCheckSpec,
  LoadingPolicyInput,
  LoadingPolicyItem,
  LoadingPolicyOption,
  LoadingPolicyParamSpec,
} from './graphql/loadingPolicyGraphql';

/** 编辑中的一个校验条目；params 的取值形态随参数类型：ENUM 为字符串，ENUM_SET 为字符串数组，LONG_SET 为数字数组 */
export type CheckFormItem = {
  key: string;
  code: string;
  params: Record<string, unknown>;
  onFail?: string;
};

export type PolicyBasicValues = {
  name: string;
  loadingUnit: string;
  destinationWaypointTypes: string[];
  routeTypes: string[];
  businessTypes: string[];
  taskTypes: string[];
  addressing: string;
  sideEffects: string[];
};

let keySeed = 0;
export const newCheckKey = (): string => {
  keySeed += 1;
  return `check-${keySeed}`;
};

export const labelOf = (options: LoadingPolicyOption[], value: string): string =>
  options.find((o) => o.value === value)?.label ?? value;

export const findSpec = (catalog: LoadingPolicyCatalog, code: string): LoadingPolicyCheckSpec | undefined =>
  catalog.checks.find((c) => c.code === code);

/** 参数在当前条目下是否出现（visibleWhen 条件） */
export const isParamVisible = (param: LoadingPolicyParamSpec, params: Record<string, unknown>): boolean =>
  !param.visibleWhen || params[param.visibleWhen.param] === param.visibleWhen.value;

/** 当前条目下应展示的参数；同名参数（如黑名单的名单）按条件只保留一个 */
export const visibleParams = (spec: LoadingPolicyCheckSpec, params: Record<string, unknown>): LoadingPolicyParamSpec[] =>
  spec.params.filter((p) => isParamVisible(p, params));

const isEmptyValue = (v: unknown): boolean =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** 改了某个参数后，清掉依赖它、且不再出现的参数值（如拦截属性从渠道切到尾程商，名单清空） */
export const updateParam = (
  spec: LoadingPolicyCheckSpec,
  params: Record<string, unknown>,
  name: string,
  value: unknown,
): Record<string, unknown> => {
  const next: Record<string, unknown> = { ...params, [name]: value };
  const dependents = new Set(spec.params.filter((p) => p.visibleWhen?.param === name).map((p) => p.name));
  dependents.forEach((dependent) => {
    // 同名参数可能有多份定义（按条件切换类型），生效的那份变了就清空，避免旧类型的值带到新类型
    const before = spec.params.find((q) => q.name === dependent && isParamVisible(q, params));
    const after = spec.params.find((q) => q.name === dependent && isParamVisible(q, next));
    if (!after || before !== after) delete next[dependent];
  });
  return next;
};

export const policyToBasicValues = (policy: LoadingPolicyItem): PolicyBasicValues => ({
  name: policy.name,
  loadingUnit: policy.loadingUnit,
  destinationWaypointTypes: policy.destinationWaypointTypes,
  routeTypes: policy.routeTypes ?? [],
  businessTypes: policy.businessTypes ?? [],
  taskTypes: policy.taskTypes ?? [],
  addressing: policy.addressing,
  sideEffects: policy.sideEffects,
});

export const policyToCheckItems = (policy: LoadingPolicyItem): CheckFormItem[] =>
  policy.checks.map((c) => {
    let params: Record<string, unknown> = {};
    try {
      params = c.paramsJson ? JSON.parse(c.paramsJson) : {};
    } catch {
      params = {};
    }
    return { key: newCheckKey(), code: c.code, params, onFail: c.onFail ?? undefined };
  });

/** 新建策略时默认带出必配校验项，参数留空待选 */
export const defaultCheckItems = (catalog: LoadingPolicyCatalog): CheckFormItem[] =>
  catalog.checks.filter((c) => c.mandatory).map((c) => ({ key: newCheckKey(), code: c.code, params: {} }));

/** 组装提交给后端的输入：只带当前可见、且有值的参数；空数组的可选字段传 null */
export const buildPolicyInput = (
  catalog: LoadingPolicyCatalog,
  basic: PolicyBasicValues,
  checks: CheckFormItem[],
): LoadingPolicyInput => ({
  name: (basic.name ?? '').trim(),
  loadingUnit: basic.loadingUnit,
  destinationWaypointTypes: basic.destinationWaypointTypes ?? [],
  routeTypes: basic.routeTypes?.length ? basic.routeTypes : null,
  businessTypes: basic.businessTypes?.length ? basic.businessTypes : null,
  taskTypes: basic.taskTypes?.length ? basic.taskTypes : null,
  addressing: basic.addressing,
  sideEffects: basic.sideEffects ?? [],
  checks: checks.map((item) => {
    const spec = findSpec(catalog, item.code);
    const params: Record<string, unknown> = {};
    (spec ? visibleParams(spec, item.params) : []).forEach((p) => {
      const v = item.params[p.name];
      if (!isEmptyValue(v)) params[p.name] = v;
    });
    return {
      code: item.code,
      paramsJson: Object.keys(params).length > 0 ? JSON.stringify(params) : null,
      onFail: item.onFail || null,
    };
  }),
});

/** 一个校验条目的简短说明，用于列表展示 */
export const describeCheck = (catalog: LoadingPolicyCatalog, code: string, paramsJson: string | null, onFail: string | null): string => {
  const spec = findSpec(catalog, code);
  if (!spec) return code;
  let params: Record<string, unknown> = {};
  try {
    params = paramsJson ? JSON.parse(paramsJson) : {};
  } catch {
    return `${spec.label}（参数无法解析）`;
  }
  const parts = visibleParams(spec, params)
    .filter((p) => !isEmptyValue(params[p.name]))
    .map((p) => {
      const v = params[p.name];
      const text = Array.isArray(v) ? v.map((x) => labelOf(p.options, String(x))).join('、') : labelOf(p.options, String(v));
      return `${p.label}：${text}`;
    });
  if (onFail && onFail !== 'BLOCK') parts.push(labelOf(spec.allowedOnFail, onFail));
  return parts.length ? `${spec.label}（${parts.join('；')}）` : spec.label;
};

/** 后端错误路径 checks[i] → 条目下标 */
export const checkIndexOfPath = (path: string): number | null => {
  const m = /^checks\[(\d+)\]/.exec(path);
  return m ? Number(m[1]) : null;
};

/** 变更记录前后快照的差异字段 */
export const diffSnapshot = (beforeJson: string | null, afterJson: string | null): string[] => {
  const parse = (s: string | null): Record<string, unknown> => {
    try {
      return s ? JSON.parse(s) : {};
    } catch {
      return {};
    }
  };
  const before = parse(beforeJson);
  const after = parse(afterJson);
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  return keys.filter((k) => k !== 'version' && JSON.stringify(before[k]) !== JSON.stringify(after[k]));
};
